import { useUpdateLostItem, useDeleteLostItem } from './useLostItems';
import { toast } from "@/components/ui/sonner";
import { LostItem } from "@/types";
import { useSupabaseAuth } from '@/contexts/SupabaseAuthContext';
import type { Json } from '@/integrations/supabase/types';

/**
 * Custom hook for lost item actions (delete, discard, deliver, undo) in Lost & Found module.
 * Handles updating item status/history and showing toasts.
 */
export const useLostItemActions = () => {
  const updateLostItem = useUpdateLostItem();
  const deleteLostItem = useDeleteLostItem();
  const { user } = useSupabaseAuth();

  /**
   * Returns the username or email of the current user for audit/history.
   */
  const getUpdatedBy = () => user?.user_metadata?.username || user?.email || 'unknown';

  /**
   * Appends a new status entry to the item's history array.
   * Used whenever an item's status changes.
   */
  const appendHistory = (item: LostItem, status: string, extra: Partial<{ receiver_name: string }>) => {
    const prevHistory = item.history || [];
    return [
      ...prevHistory,
      {
        status,
        updatedBy: getUpdatedBy(),
        date: new Date().toISOString(),
        ...(extra.receiver_name ? { receiverName: extra.receiver_name } : {}),
      },
    ];
  };

  /**
   * Marks a lost item as deleted (soft delete) and updates its history.
   */
  const handleDelete = async (item: LostItem) => {
    if (!item || !item.id) {
      console.error('[useLostItemActions] Tried to delete item with undefined id:', item);
      toast("Failed to delete item", { description: "Item is missing id." });
      return;
    }
    try {
      await updateLostItem.mutateAsync({
        id: item.id,
        changes: {
          status: "Deleted",
          is_deleted: true,
          deletion_requested_at: new Date().toISOString(),
          history: appendHistory(item, "Deleted", {}) as Json,
        },
      });
      const expKey = `lostitem_delete_${item.id}`;
      localStorage.setItem(expKey, (Date.now() + 1000 * 3600 * 48).toString());
      toast("Item Deleted", {
        description: "Will be removed permanently after 48hrs if not restored."
      });
    } catch (error) {
      console.error('Delete error:', error);
      toast("Failed to delete item", { description: error?.message || String(error) });
    }
  };

  /**
   * Marks a lost item as discarded and updates its history.
   */
  const handleDiscard = async (item: LostItem) => {
    if (!item || !item.id) {
      console.error('[useLostItemActions] Tried to discard item with undefined id:', item);
      toast("Failed to discard item", { description: "Item is missing id." });
      return;
    }
    try {
      await updateLostItem.mutateAsync({
        id: item.id,
        changes: {
          status: "Discarded",
          history: appendHistory(item, "Discarded", {}) as Json,
        },
      });
      toast("Success", {
        description: "Item discarded successfully"
      });
    } catch (error) {
      console.error('Discard error:', error);
      toast("Failed to discard item", { description: error?.message || String(error) });
    }
  };

  /**
   * Marks a lost item as delivered and updates its history (with receiver name).
   */
  const handleDeliver = async (item: LostItem, receiver: string) => {
    if (!item || !item.id) {
      console.error('[useLostItemActions] Tried to deliver item with undefined id:', item);
      toast("Failed to mark as delivered", { description: "Item is missing id." });
      return;
    }
    try {
      await updateLostItem.mutateAsync({
        id: item.id,
        changes: {
          status: "Delivered",
          receiver_name: receiver,
          history: appendHistory(item, "Delivered", { receiver_name: receiver }) as Json,
        },
      });
      toast("Success", {
        description: "Item marked as delivered"
      });
    } catch (error) {
      console.error('Deliver error:', error);
      toast("Failed to mark as delivered", { description: error?.message || String(error) });
    }
  };

  /**
   * Reverts a lost item to Pending status and updates its history.
   */
  const handleUndo = async (item: LostItem) => {
    if (!item || !item.id) {
      console.error('[useLostItemActions] Tried to undo item with undefined id:', item);
      toast("Failed to revert status", { description: "Item is missing id." });
      return;
    }
    try {
      await updateLostItem.mutateAsync({
        id: item.id,
        changes: {
          status: "Pending",
          is_deleted: false,
          deletion_requested_at: null,
          receiver_name: undefined,
          history: appendHistory(item, "Pending", {}) as Json,
        },
      });
      toast("Status Reverted", {
        description: "Item status has been reverted to Pending"
      });
    } catch (error) {
      console.error('Undo error:', error);
      toast("Failed to revert status", { description: error?.message || String(error) });
    }
  };

  return {
    handleDelete,
    handleDiscard,
    handleDeliver,
    handleUndo,
  };
};
